import {
  type ActiveElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  type ChartEvent,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import classNames from "classnames";
import * as React from "react";

import { ChartDisplayType } from "@/shared/domain/metrics";

import styles from "./MetricChart.module.css";

// Register only the Chart.js pieces this chart uses (matches the explicit
// registration style in PlotRenderer.ts). Registration is global + idempotent.
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

// Canvas can't resolve CSS custom properties, so read the concrete color off
// the element at draw time, with a fallback matching --primary-main.
const FALLBACK_COLOR = "rgb(2, 136, 209)";

export interface PlotDatum {
  /** Bucket start, Unix-epoch nanoseconds. `bigint` to keep full ns precision:
   *  Chart.js never reads these (the x-axis is category-scaled off `label`),
   *  they only ride along for the click → drill-down query. */
  start_time: bigint;
  /** Bucket end, Unix-epoch nanoseconds. See `start_time`. */
  end_time: bigint;
  /** Absent on placeholder rows; present on real aggregate buckets. */
  value?: number;
  /** Pre-formatted x-axis label (short ISO date). */
  label: string;
  /** Pre-formatted tooltip title (start–end range). */
  tooltipTitle: string;
}

export interface MetricChartViewProps {
  /** Pre-computed, gap-filled series with formatted labels. */
  series: PlotDatum[];
  metricName: string;
  /** Unit of measure for the plotted values; titles the y-axis. Omit if unitless. */
  unit?: string | null;
  /** Line (default) or bar rendering. */
  display?: ChartDisplayType;
  /** Fired when a real (non-placeholder) bucket is clicked. */
  onBucketClick?: (bucket: { start_time: bigint; end_time: bigint }) => void;
}

/**
 * Chart presentation layer for metrics.
 *
 * Renders a Chart.js canvas from an already-computed {@link PlotDatum} series,
 * or an empty-state message when the series is empty.
 *
 * Does no data fetching or transformation — that belongs in the {@link MetricChart} wrapper.
 */
export function MetricChartView({
  series,
  metricName,
  unit = null,
  display = "line",
  onBucketClick,
}: MetricChartViewProps) {
  const chartRef = React.useRef<Chart | null>(null);

  // The chart is created once per `display` type and then mutated in place (see
  // the update effect below) as the series / metric name change, so it — and
  // its option callbacks — outlive those data changes. The callbacks therefore
  // read the latest values through refs instead of closing over stale ones.
  const clickRef = React.useRef(onBucketClick);
  clickRef.current = onBucketClick;
  const seriesRef = React.useRef(series);
  seriesRef.current = series;
  const metricNameRef = React.useRef(metricName);
  metricNameRef.current = metricName;
  const unitRef = React.useRef(unit);
  unitRef.current = unit;

  // Build (and tear down) the chart as the <canvas> mounts and whenever
  // `display` changes — Chart.js can't switch an existing chart's type, so a
  // type change is the one case that needs a fresh instance. Series / metric
  // name changes never reach here; they flow through chart.update() below. The
  // canvas is absent in the empty state, so this also disposes the chart when
  // the series empties out.
  const setCanvas = React.useCallback(
    (canvas: HTMLCanvasElement | null) => {
      chartRef.current?.destroy();
      chartRef.current = null;
      if (!canvas) {
        return;
      }

      const color =
        getComputedStyle(canvas).getPropertyValue("--primary-main").trim() ||
        FALLBACK_COLOR;

      chartRef.current = new Chart(canvas, {
        type: display === "bar" ? "bar" : "line",
        data: {
          labels: seriesRef.current.map((d) => d.label),
          datasets: [
            {
              label: metricNameRef.current,
              data: seriesRef.current.map((d) => d.value ?? null),
              borderColor: color,
              backgroundColor: color,
              // Connect across missing buckets (rendered as null) instead of
              // leaving gaps; fillMissingBuckets still keeps the x-axis spacing
              // time-proportional.
              spanGaps: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: { mode: "index", intersect: false },
          onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
            const index = elements[0]?.index;
            if (index === undefined) {
              return;
            }
            const datum = seriesRef.current[index];
            if (datum && datum.value !== undefined) {
              clickRef.current?.({
                start_time: datum.start_time,
                end_time: datum.end_time,
              });
            }
          },
          scales: {
            x: { ticks: { autoSkip: true, maxRotation: 0 } },
            y: {
              beginAtZero: true,
              title: {
                display: Boolean(unitRef.current),
                text: unitRef.current ?? "",
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const index = items[0]?.dataIndex;
                  if (index === undefined) {
                    return "";
                  }
                  return seriesRef.current[index]?.tooltipTitle ?? "";
                },
                label: (item) => {
                  const y = item.parsed.y;
                  if (typeof y !== "number" || Number.isNaN(y)) {
                    return "—";
                  }
                  return unitRef.current
                    ? `${String(y)} ${unitRef.current}`
                    : String(y);
                },
              },
            },
          },
        },
      });
    },
    [display],
  );

  // Push series / metric-name / unit changes into the existing chart rather than
  // rebuilding it (Chart.js's recommended path for data updates). The tooltip
  // callback reads the unit off its ref, but the y-axis title is baked into the
  // options object at build time, so it has to be reassigned here.
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    chart.data.labels = series.map((d) => d.label);
    const [dataset] = chart.data.datasets;
    if (dataset) {
      dataset.data = series.map((d) => d.value ?? null);
      dataset.label = metricName;
    }
    // `scales.y` is typed as a union over every scale kind (linear, time,
    // radial…), so `title` isn't reachable without narrowing to the cartesian
    // shape we actually build above.
    const yScale = chart.options.scales?.y as
      | { title?: { display?: boolean; text?: string } }
      | undefined;
    if (yScale?.title) {
      yScale.title.display = Boolean(unit);
      yScale.title.text = unit ?? "";
    }
    chart.update();
  }, [series, metricName, unit]);

  if (series.length === 0) {
    return (
      <p className={styles.empty}>No data points in the selected window.</p>
    );
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.clickable]: Boolean(onBucketClick),
      })}
    >
      <canvas ref={setCanvas} role="img" aria-label={`${metricName} chart`} />
    </div>
  );
}
